package com.upcominds.expedite.web

import androidx.compose.runtime.*
import com.upcominds.expedite.di.getKoin
import com.upcominds.expedite.features.energy.*
import kotlinx.datetime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun CommunityEnergyAdminPanel(communityId: String, platformAdmin: Boolean) {
    val viewModel: CommunityEnergyViewModel = remember { getKoin().get() }
    val state by viewModel.uiState.collectAsStateWeb()
    val data = state.data?.takeIf { it.communityId == communityId }
    var buildingName by remember(communityId) { mutableStateOf("") }
    var tab by remember(communityId) { mutableStateOf("electricity_consumption_kwh") }
    var days by remember(communityId) { mutableStateOf(7) }
    val busy = state.busy || state.loading
    val metrics = data?.buildingMetrics().orEmpty()
    val metric = metrics.firstOrNull { it.id == tab }
    val today = Clock.System.now().toLocalDateTime(TimeZone.UTC).date
    val from = today.minus(days, DateTimeUnit.DAY)
    val readings = metric?.readings.orEmpty().filter { LocalDate.parse(it.periodStart.take(10)) >= from && LocalDate.parse(it.periodStart.take(10)) < today }

    LaunchedEffect(communityId) { viewModel.loadAdmin(communityId) }
    LaunchedEffect(data?.buildingName) { buildingName = data?.buildingName.orEmpty() }

    Div({ classes(DashboardStyles.card) }) {
        H3({ style { marginTop(0.px) } }) { Text("Building Data") }
        P({ classes(DashboardStyles.mutedText) }) { Text("Energy and building measurements · Completed daily aggregates, not live meter readings.") }
        if (state.error != null && state.communityId == communityId) {
            P({ style { color(Color("#b91c1c")) }; attr("role", "alert") }) { Text(state.error!!) }
        }
        if (state.loading && data == null) P { Text("Loading building data…") }
        Div({
            attr("role", "tablist"); attr("aria-label", "Building data")
            style { display(DisplayStyle.Flex); gap(8.px); property("overflow-x", "auto"); paddingBottom(12.px); marginBottom(12.px); property("border-bottom", "1px solid #e5e7eb") }
        }) {
            val tabs = metrics.map { it.id to it.label } + if (platformAdmin) listOf("connection" to "Connection & sync") else emptyList()
            tabs.forEach { (id, label) ->
                Button({
                    classes(if (tab == id) DashboardStyles.button else DashboardStyles.secondaryButton)
                    attr("role", "tab"); attr("aria-selected", (tab == id).toString()); attr("aria-controls", "building-panel-$communityId")
                    style { property("white-space", "nowrap"); property("flex-shrink", "0") }
                    onClick { tab = id }
                }) { Text(label) }
            }
        }
        Div({ id("building-panel-$communityId"); attr("role", "tabpanel") }) {
            if (tab == "connection" && platformAdmin) {
                Label(forId = "energy-building-$communityId") { Text("Fusion building name") }
                Input(InputType.Text) {
                    id("energy-building-$communityId"); classes(DashboardStyles.textInput); value(buildingName)
                    placeholder("e.g. Azenes 12/1"); attr("maxlength", "200"); if (busy) disabled()
                    onInput { buildingName = it.value }
                }
                P({ classes(DashboardStyles.mutedText) }) { Text("Use the exact source building name. One daily sync imports all six measurements. The initial import covers 30 days; later syncs revisit the latest three days for corrections.") }
                Div({ style { display(DisplayStyle.Flex); gap(12.px); property("flex-wrap", "wrap") } }) {
                    Button({ classes(DashboardStyles.button); if (busy || buildingName.isBlank()) disabled(); onClick { viewModel.connect(communityId, buildingName.trim()) } }) {
                        Text(if (state.busy) "SYNCHRONIZING…" else "SAVE & SYNC")
                    }
                    if (data?.buildingName != null) {
                        Button({ classes(DashboardStyles.secondaryButton); if (busy) disabled(); onClick { viewModel.sync(communityId) } }) { Text("SYNC NOW") }
                        Button({ classes(DashboardStyles.secondaryButton); if (busy) disabled(); onClick { viewModel.connect(communityId, null) } }) { Text("DISCONNECT") }
                    }
                }
            } else if (data != null && data.buildingName == null) {
                P { Text("No building is connected to this community.") }
                if (platformAdmin) Button({ classes(DashboardStyles.button); onClick { tab = "connection" } }) { Text("CONNECT A BUILDING") }
            } else if (metric != null) {
                H4 { Text(if (metric.unit == "kWh") "Daily electricity consumption" else "${metric.label} · Daily building measurements") }
                metric.readings.lastOrNull()?.let { latest ->
                    P { B { Text("Latest available day: ") }; Text("${latest.periodStart.take(10)} · ${metric.format(latest.value)} ${metric.unit}") }
                }
                Div({ style { display(DisplayStyle.Flex); gap(8.px) } }) {
                    listOf(7, 30).forEach { option ->
                        Button({ classes(if (days == option) DashboardStyles.button else DashboardStyles.secondaryButton); onClick { days = option } }) { Text("$option days") }
                    }
                }
                val summary = metric.summary(readings)
                if (summary == null) P { Text("No ${metric.label.lowercase()} readings are available for this period.") }
                else {
                    P {
                        B { Text(if (metric.summaryType == "SUM") "Recorded total: " else "Average of available daily readings: ") }
                        Text("${metric.format(summary)} ${metric.unit} · ${readings.size}/$days days with data")
                    }
                    Div({
                        classes("admin-table-scroll"); attr("role", "region"); attr("aria-label", "Daily building measurements")
                        attr("tabindex", "0")
                        style { property("max-height", "300px"); property("overflow", "auto") }
                    }) {
                        Table({ style { width(100.percent); property("border-collapse", "collapse") } }) {
                            Thead { Tr {
                                Th({ style { textAlign("left"); padding(8.px); property("position", "sticky"); top(0.px); backgroundColor(Color.white) } }) { Text("Day (UTC)") }
                                Th({ style { textAlign("right"); padding(8.px); property("position", "sticky"); top(0.px); backgroundColor(Color.white) } }) { Text("${metric.label} (${metric.unit})") }
                            } }
                            Tbody { readings.reversed().forEach { reading ->
                                Tr {
                                    Td({ style { padding(8.px); property("border-top", "1px solid #e5e7eb") } }) { Text(reading.periodStart.take(10)) }
                                    Td({ style { padding(8.px); textAlign("right"); property("border-top", "1px solid #e5e7eb") } }) { Text(metric.format(reading.value)) }
                                }
                            } }
                        }
                    }
                }
            }
        }
        if (data?.buildingName != null) {
            P({ classes(DashboardStyles.mutedText) }) { Text("${data.buildingName} · Last successful sync: ${energyTimestamp(data.lastSyncAt)}") }
            if (data.lastError != null) {
                P({ style { color(Color("#b91c1c")) }; attr("role", "alert") }) { Text(data.lastError) }
                P({ classes(DashboardStyles.mutedText) }) { Text("Last attempt: ${energyTimestamp(data.lastAttemptAt)}. Previously synchronized readings are kept.") }
            }
            P({ classes(DashboardStyles.mutedText) }) { Text("Source: ${data.source}. Missing readings are unknown, not zero. Period averages use available daily values.") }
        }
        Button({ classes(DashboardStyles.secondaryButton); if (busy) disabled(); onClick { viewModel.loadAdmin(communityId) } }) { Text(if (state.loading) "REFRESHING…" else "REFRESH") }
    }
}
